// Group runs: several riders share one van to/from the same place.
import { tripFlags } from "@/lib/tripCodes";
const R = 3958.8;
const rad = (d) => (d * Math.PI) / 180;
export const miles = (a, b) => {
  if (a?.lat == null || b?.lat == null) return 5;
  const x = Math.sin(rad(b.lat - a.lat) / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(rad(b.lng - a.lng) / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
};
const driveMin = (a, b) => Math.round((miles(a, b) * 1.3 / 22) * 60);
const DWELL = 3;
export const WINDOW_MIN = 15;
export const MAX_RIDE_MIN = 45;

export const isAba = (t) => tripFlags(t).aba || /\baba\b|autism|behavior/i.test([t.trip_type, t.dropoff_name, t.dropoff_address, t.special_instructions, t.mobility_notes, t.broker].join(" "));
const pt = (lat, lng) => ({ lat, lng });
const key = (addr, lat, lng) => {
  const a = String(addr || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  return a || (lat != null && lng != null ? `${lat.toFixed(3)},${lng.toFixed(3)}` : "");
};
export const seatsFor = (trips) => trips.reduce((s, t) => { const f = tripFlags(t); return s + 1 + (f.escort ? 1 : 0) + (f.monitor ? 1 : 0); }, 0);

// Order stops nearest-first and estimate each rider's time in the van.
function plan(direction, trips, center) {
  const left = [...trips];
  const order = [];
  if (direction === "to") {
    // start with the farthest pickup from the center, then hop to the nearest next one
    left.sort((a, b) => miles(pt(b.pickup_lat, b.pickup_lng), center) - miles(pt(a.pickup_lat, a.pickup_lng), center));
    order.push(left.shift());
    while (left.length) {
      const last = order[order.length - 1];
      left.sort((a, b) => miles(pt(last.pickup_lat, last.pickup_lng), pt(a.pickup_lat, a.pickup_lng)) - miles(pt(last.pickup_lat, last.pickup_lng), pt(b.pickup_lat, b.pickup_lng)));
      order.push(left.shift());
    }
    let t = 0;
    const at = order.map((x, i) => { if (i) t += driveMin(pt(order[i - 1].pickup_lat, order[i - 1].pickup_lng), pt(x.pickup_lat, x.pickup_lng)) + DWELL; return t; });
    const end = t + DWELL + driveMin(pt(order[order.length - 1].pickup_lat, order[order.length - 1].pickup_lng), center);
    return { order, stops: order.map((x, i) => ({ trip: x, offset: at[i], ride: end - at[i] })), total: end };
  }
  let prev = center;
  while (left.length) {
    left.sort((a, b) => miles(prev, pt(a.dropoff_lat, a.dropoff_lng)) - miles(prev, pt(b.dropoff_lat, b.dropoff_lng)));
    const n = left.shift(); order.push(n); prev = pt(n.dropoff_lat, n.dropoff_lng);
  }
  let t = DWELL;
  const stops = order.map((x, i) => { t += driveMin(i ? pt(order[i - 1].dropoff_lat, order[i - 1].dropoff_lng) : center, pt(x.dropoff_lat, x.dropoff_lng)) + (i ? DWELL : 0); return { trip: x, offset: t, ride: t }; });
  return { order, stops, total: t };
}

// Trim the rider with the longest ride until everyone is under the limit.
function fit(direction, trips, center, maxSeats) {
  let keep = [...trips];
  const out = [];
  while (keep.length > 1) {
    const p = plan(direction, keep, center);
    const worst = p.stops.reduce((w, s) => (s.ride > w.ride ? s : w), p.stops[0]);
    if (worst.ride <= MAX_RIDE_MIN && seatsFor(keep) <= maxSeats) return { run: { ...p, trips: keep }, rest: out };
    const drop = seatsFor(keep) > maxSeats ? p.stops[p.stops.length - 1].trip : worst.trip;
    keep = keep.filter((t) => t.id !== drop.id);
    out.push(drop);
  }
  return { run: null, rest: [...out, ...keep] };
}

export function suggestRuns(trips, { abaOnly, maxSeats }) {
  // riders coded "ride alone" (1-on-1, stretcher, PT-1 approved) never share a van
  const pool = trips.filter((t) => t.scheduled_pickup && !tripFlags(t).alone && (!abaOnly || isAba(t)));
  const groups = {};
  pool.forEach((t) => {
    const to = key(t.dropoff_address, t.dropoff_lat, t.dropoff_lng);
    const from = key(t.pickup_address, t.pickup_lat, t.pickup_lng);
    (groups[`to|${to}`] = groups[`to|${to}`] || []).push(t);
    (groups[`from|${from}`] = groups[`from|${from}`] || []).push(t);
  });
  const used = new Set();
  const runs = [];
  // biggest shared-destination groups first so a trip joins its best run
  Object.entries(groups).sort((a, b) => b[1].length - a[1].length).forEach(([k, list]) => {
    const direction = k.startsWith("to") ? "to" : "from";
    let open = list.filter((t) => !used.has(t.id)).sort((a, b) => a.scheduled_pickup.localeCompare(b.scheduled_pickup));
    while (open.length > 1) {
      const t0 = new Date(open[0].scheduled_pickup).getTime();
      const win = open.filter((t) => new Date(t.scheduled_pickup).getTime() - t0 <= WINDOW_MIN * 60000);
      if (win.length < 2) { open = open.slice(1); continue; }
      const f = open[0];
      const center = direction === "to" ? pt(f.dropoff_lat, f.dropoff_lng) : pt(f.pickup_lat, f.pickup_lng);
      const { run } = fit(direction, win, center, maxSeats);
      if (!run) { open = open.slice(1); continue; }
      run.trips.forEach((t) => used.add(t.id));
      runs.push({
        id: `${direction}-${run.trips.map((t) => t.id).sort().join("-")}`,
        direction, center,
        centerName: direction === "to" ? (f.dropoff_name || f.dropoff_address) : (f.pickup_address),
        start: run.trips.map((t) => t.scheduled_pickup).sort()[0],
        stops: run.stops, total: run.total,
        seats: seatsFor(run.trips), wheelchair: run.trips.some((t) => tripFlags(t).wheelchair),
      });
      open = open.filter((t) => !used.has(t.id));
    }
  });
  return runs.sort((a, b) => a.start.localeCompare(b.start));
}

// Nearest free driver whose own vehicle has the seats (and ramp) the run needs.
export function pickDriver(run, drivers, busy, taken) {
  const startMs = new Date(run.start).getTime();
  const endMs = startMs + (run.total + 15) * 60000;
  const first = run.direction === "to" ? pt(run.stops[0].trip.pickup_lat, run.stops[0].trip.pickup_lng) : run.center;
  const early = new Date(run.start).getHours() < 10;
  return drivers
    .filter((d) => d.veh && (d.veh.capacity || 0) >= run.seats && (!run.wheelchair || d.veh.wheelchair_accessible))
    .filter((d) => !(busy[d.id] || []).some((ms) => ms >= startMs - 30 * 60000 && ms <= endMs))
    .filter((d) => !(taken[d.id] || []).some(([s, e]) => s < endMs && startMs < e))
    .map((d) => ({ d, dist: miles(early && d.home_lat != null ? pt(d.home_lat, d.home_lng) : pt(d.current_lat, d.current_lng), first) }))
    .sort((a, b) => a.dist - b.dist)[0]?.d || null;
}
